// ─────────────────────────────────────────────────────────────────────────────
// Profile Settings guide page
//
// Reference for the Profile page. Covers each of the five blocks visible
// on screen and what they do.
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideProfile() {
  return (
    <GuideLayout
      title="Profile Settings"
      subtitle="Manage your display name, dashboard layout, data export, instrument settings, and sign out."
      prev={{ slug: "market",  label: "Market" }}
      next={{ slug: "billing", label: "Billing" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Profile Settings is the configuration page for everything
        account-level — your identity in the app, your data, your
        per-account instrument settings, and your session. It's not
        where you go often, but it's where you go for the things you
        only do occasionally.
      </P>
      <P>
        Open it from the sidebar (👤 Profile) or from the user avatar
        at the bottom of the sidebar drawer.
      </P>

      <ScreenshotFrame
        src="/guide/profile-settings.png"
        alt="Profile Settings page with account, trading, data, and session blocks"
        caption="Profile Settings — six blocks, each handling one category of account configuration."
      />

      <H2>Account</H2>
      <P>
        Your name and email at the top, with a <UI>Display Name</UI>{" "}
        field below. Edit the name and click <UI>Save Changes</UI>{" "}
        to update what shows up in the app's greeting (e.g. the
        Dashboard's "Good evening, [name]").
      </P>
      <P>
        Email is shown but not editable here — it's tied to your
        account at the auth layer. If you need to change your email,
        contact support.
      </P>

      <H2>Appearance</H2>
      <P>
        The <UI>Dashboard Layout</UI> selector lets you switch between
        two layout modes for the Dashboard:
      </P>
      <UL>
        <LI>
          <strong>Vertical</strong> — the default. Sections stack
          top to bottom and you scroll to see everything. Works on any
          screen size.
        </LI>
        <LI>
          <strong>Horizontal</strong> — a three-column layout that puts
          everything side by side, with no scrolling needed on a wide
          desktop screen.
        </LI>
      </UL>
      <P>
        Click either option and it saves immediately — no separate Save
        button needed. The Dashboard switches as soon as you navigate
        back to it.
      </P>

      <Callout kind="tip">
        The horizontal layout works best on screens wider than 1024px.
        On narrower screens or mobile devices, it adapts automatically —
        portrait orientation stacks the columns vertically, and landscape
        shows a compressed three-column view. If you primarily use Futures
        OS on a phone, stick with the vertical layout.
      </Callout>

      <H2>Trading Rules</H2>
      <P>
        A pointer card with a <UI>🛡️ Manage Trading Rules</UI> button
        that takes you to the per-account rules configuration. This
        is the same destination as the "Open Rules →" card on the
        Strategies page.
      </P>
      <P>
        See the <strong>Portfolio & Trading Rules</strong> guide for
        the full rundown of what you can configure there.
      </P>

      <H2>Data Management</H2>
      <P>
        The data export block downloads your trades as a CSV. Click{" "}
        <UI>Export N trades to CSV</UI> (where N is your total trade
        count across all accounts) to open the export options. The
        button is disabled if you have no trades yet.
      </P>
      <P>
        The CSV is analytics-focused: it includes date, time, symbol,
        direction, entry/exit prices, size, P&L (gross and net), fees,
        commissions, result, setup, risk/reward, duration, and notes.
        It's UTF-8 with a BOM so it opens cleanly in Excel. The export
        is designed to round-trip — re-importing a CSV you exported
        should reconstruct your trades.
      </P>
      <P>
        A few things the CSV doesn't currently capture: which account
        a trade belonged to, behavioral fields (fear, focus,
        conviction), and post-trade reflection notes when separate
        from your regular notes. For full archival you'll want the
        in-app history; the CSV is best thought of as an analytics
        and tax-prep view.
      </P>

      <Callout kind="info" title="Why you might export">
        Three common reasons: backup before making major changes,
        analyzing your trades in Excel or a Python/R script, or
        moving to another platform. The CSV is yours — Futures OS
        doesn't lock you in.
      </Callout>

      <H2>Instrument Settings</H2>
      <P>
        Below Data Management, the <UI>Instrument Settings for This
        Account</UI> section lets you configure tick sizes and tick
        values for the instruments you trade. Click{" "}
        <UI>+ Add Instrument</UI> to add a new one with custom
        settings.
      </P>
      <P>
        Two visual cues in this section:
      </P>
      <UL>
        <LI><strong>Green cards</strong> — instrument has custom settings configured</LI>
        <LI><strong>Gray cards</strong> — instrument is using the system's built-in default settings for that symbol</LI>
      </UL>
      <P>
        Tick size and tick value are used to calculate P&L from your
        entry and exit prices: <em>(exit − entry) / tick_size × tick_value
        × position_size</em>.
      </P>

      <H2>Built-in instrument defaults</H2>
      <P>
        Futures OS ships with tick definitions for 26 commonly-traded
        futures contracts across major asset classes:
      </P>
      <UL>
        <LI><strong>Equity indices:</strong> ES, NQ, YM, RTY (E-minis) and MES, MNQ, MYM, M2K (micros)</LI>
        <LI><strong>Energy:</strong> CL, MCL, NG</LI>
        <LI><strong>Metals:</strong> GC, MGC, SI</LI>
        <LI><strong>Rates:</strong> ZN, ZB</LI>
        <LI><strong>Currencies:</strong> 6E, 6B</LI>
        <LI><strong>Grains:</strong> ZC, ZS, ZW</LI>
        <LI><strong>Crypto:</strong> BTC, MBT, ETH, MET</LI>
      </UL>
      <P>
        Broker symbols with month/year suffixes (like <UI>MNQH25</UI>{" "}
        for the March 2025 MNQ contract) are matched automatically by
        prefix — you don't need to add every contract month.
      </P>
      <P>
        Symbols not on the list still work. They fall back to a
        default tick size of 0.01 and tick value of $1.00, which you
        can override by adding a custom instrument with the right
        values for your contract.
      </P>

      <Callout kind="info" title="Instrument settings apply across all your accounts">
        Custom instrument settings are stored per-user, not
        per-account. If you override the tick size for NQ once, that
        override applies whether you're trading your TopStep combine,
        your funded account, or a sim. This keeps you from having
        to re-configure the same contract on every account you add.
      </Callout>

      <H2>Session</H2>
      <P>
        The bottom block has a <UI>Sign Out</UI> button. Clicking it
        ends your session — you'll need to log back in to access
        your data.
      </P>
      <P>
        Signing out doesn't delete anything; your trades, rules,
        accounts, and settings are all stored server-side and load
        back the next time you sign in.
      </P>

      <H2>Reset app & clear local data</H2>
      <P>
        Below the Sign Out block, a red <UI>Reset app & clear local
        data</UI> link clears everything Futures OS keeps in your
        browser — cached trade data, draft state, UI preferences,
        and your auth tokens. Then it reloads the page.
      </P>
      <P>
        Because auth tokens are part of what gets cleared, hitting
        reset signs you out as a side effect. You'll land on the
        login screen after the reload, and need to sign back in.
      </P>

      <Callout kind="warn" title="Server data is safe — local session is not">
        Reset only touches browser-side data. Your trades, rules,
        accounts, and settings all live in the database and rebuild
        from the server when you sign back in. But you <em>will</em>{" "}
        be signed out, so make sure you have your password handy
        before clicking. Use this when the app feels stuck after an
        update or behaves oddly — it's a clean-slate reload, not a
        light cache flush.
      </Callout>

      <H2>Danger Zone — Delete account</H2>
      <P>
        At the very bottom of Profile Settings, a red-bordered card
        labeled <strong>DANGER ZONE</strong> holds the{" "}
        <UI>Delete my account</UI> button. This is permanent account
        deletion — distinct from signing out (which keeps your
        account) and from Reset (which just clears the browser cache).
      </P>
      <P>
        Clicking <UI>Delete my account</UI> opens a confirmation
        dialog that explains exactly what will happen, then requires
        you to type <UI>DELETE</UI> in uppercase to enable the final
        button. The deliberate friction is intentional — this isn't
        an action you can undo casually.
      </P>

      <H2>What happens when you delete</H2>
      <P>
        Confirming the deletion does four things, in order:
      </P>
      <UL>
        <LI><strong>Your account is marked for deletion immediately.</strong> A <UI>deleted_at</UI> timestamp goes on your profile.</LI>
        <LI><strong>Your subscription is set to cancel at the end of the current billing period.</strong> No immediate refund, but no further charges either. You keep paid access until what you've already paid for runs out.</LI>
        <LI><strong>You're signed out of all sessions</strong> — every browser, every device.</LI>
        <LI><strong>A 30-day grace period begins.</strong> Your data is kept (trades, accounts, strategies, settings, history — everything) in case you change your mind.</LI>
      </UL>
      <P>
        After the 30-day grace period expires, a nightly job
        permanently deletes everything: your trades, accounts, custom
        strategies, instrument settings, rules, journal entries — and
        your Stripe customer record. Your billing history (invoices)
        is retained by Stripe for tax-compliance reasons even after
        this, but it's no longer linked to an account you can access.
      </P>

      <H2>Changing your mind — the 30-day restore window</H2>
      <P>
        At any point within 30 days of clicking Delete, you can sign
        back in with the same email and password. Instead of the
        normal app, you'll see a <strong>Welcome back</strong> screen
        showing the scheduled deletion date and how many days are
        left to restore.
      </P>
      <P>
        Click <UI>Restore my account</UI> and three things happen:
      </P>
      <UL>
        <LI>Your <UI>deleted_at</UI> flag is cleared — your account is fully active again</LI>
        <LI>If your subscription is still in its grace window (cancel-at-period-end hasn't kicked in yet), it's reactivated — billing continues as normal</LI>
        <LI>If your subscription has already canceled (you waited past the period end before restoring), you're returned to the upgrade gate to start a new subscription</LI>
      </UL>
      <P>
        After the restore screen confirms what happened to your
        subscription, click <UI>Continue to app</UI> to return to the
        Dashboard. Everything is exactly where you left it.
      </P>

      <Callout kind="warn" title="No refunds, even on deletion">
        Deletion cancels your subscription at the end of your current
        billing period — you keep paid access through that date. You
        will <em>not</em> receive a refund for any unused portion of
        the current paid period. State your intent to leave clearly
        before clicking; the no-refund policy holds whether the
        cancellation came through the Stripe portal or through
        account deletion.
      </Callout>

      <Callout kind="info" title="Restoring after subscription expiry">
        If you delete on day 5 of a 30-day trial and don't restore
        until day 32, your trial has already ended in Stripe. Restore
        will still bring back your trades and settings, but you'll
        land on the upgrade gate and need to start a new subscription
        to regain full access. The data is restored; the billing is not.
      </Callout>

      <H2>What's not here</H2>
      <P>
        A few settings you might expect to find here actually live
        elsewhere:
      </P>
      <UL>
        <LI><strong>Adding/removing trading accounts</strong> — that's on the <UI>Portfolio</UI> page</LI>
        <LI><strong>Subscription management</strong> (changing plan, updating payment method, viewing invoices) — that's on the <UI>Billing</UI> page (next in this guide). Account deletion <em>does</em> cancel your subscription, but the granular management lives on Billing.</LI>
        <LI><strong>Strategy visibility toggles</strong> — that's on the <UI>Strategies</UI> page</LI>
        <LI><strong>Dashboard layout preference</strong> — this one <em>is</em> here, in the Appearance section above</LI>
        <LI><strong>Privacy and legal documents</strong> — accessible from the sign-up footer and a dedicated route at <UI>/legal/privacy</UI> and <UI>/legal/terms</UI></LI>
        <LI><strong>Changing your email address</strong> — not currently supported in-app; contact support if you need this</LI>
      </UL>
    </GuideLayout>
  );
}
